import { motion, useMotionValue, useSpring, useReducedMotion, useMotionValueEvent } from "framer-motion";
import { useEffect, useState, useId, useRef } from "react";
import { CountUp } from "@/components/VoteFX";

const BLUE = "#7B2CBF";
const CORAL = "#FFC300";
const COMMENTS_PER_VOTE = 5;

export default function TheFlip({ votesA, votesB, voteAnim, kickSide = null, userSide = null, commentsA = 0, commentsB = 0, size = "large", labelA = "View A", labelB = "View B", compact = false }) {
  // Effective tallies: 5 comments on a side = 1 vote-equivalent
  const cvA = commentsA / COMMENTS_PER_VOTE;
  const cvB = commentsB / COMMENTS_PER_VOTE;
  const effA = votesA + cvA;
  const effB = votesB + cvB;
  const effTotal = effA + effB;
  const pctA = effTotal > 0 ? (effA / effTotal) * 100 : 50;
  const pctB = 100 - pctA;

  const voteTotal = votesA + votesB;
  const votePctA = voteTotal > 0 ? (votesA / voteTotal) * 100 : 50;
  const lead = pctA >= pctB ? BLUE : CORAL;

  const targetAngle = ((pctB - 50) / 50) * 90;          // effective needle
  const ghostAngle = (((100 - votePctA) - 50) / 50) * 90; // votes-only ghost
  const commentDelta = Math.round(pctA - votePctA);      // how much comments moved it toward A

  const reduced = useReducedMotion();
  const motionAngle = useMotionValue(targetAngle);
  const springAngle = useSpring(motionAngle, { stiffness: 55, damping: 11, mass: 1.1 });
  const needleRef = useRef(null);

  const [rippleKey, setRippleKey] = useState(0);
  const uid = useId().replace(/:/g, "");

  const CX = 150, CY = 155;
  useMotionValueEvent(springAngle, "change", (v) => {
    needleRef.current?.setAttribute("transform", `rotate(${v} ${CX} ${CY})`);
  });
  useEffect(() => {
    if (voteAnim > 0 && kickSide && !reduced) {
      setRippleKey((k) => k + 1);
      const dir = kickSide === "b" ? 1 : -1;
      motionAngle.set(targetAngle + dir * 17);
      const t = setTimeout(() => motionAngle.set(targetAngle), 240);
      return () => clearTimeout(t);
    }
    motionAngle.set(targetAngle);
  }, [voteAnim, targetAngle, kickSide, reduced, motionAngle]);

  const R = 118;
  const pt = (t) => {
    const a = (180 + t * 180) * Math.PI / 180;
    return [CX + R * Math.cos(a), CY + R * Math.sin(a)];
  };
  const arcPath = (t0, t1) => {
    const [x0, y0] = pt(t0), [x1, y1] = pt(t1);
    return `M ${x0.toFixed(2)} ${y0.toFixed(2)} A ${R} ${R} 0 0 1 ${x1.toFixed(2)} ${y1.toFixed(2)}`;
  };
  const fracA = Math.max(0, Math.min(1, pctA / 100));
  const [trackL0, trackL1] = [pt(0), pt(1)];
  const NEEDLE = R - 30;
  const commentsCounted = Math.floor(commentsA / COMMENTS_PER_VOTE) + Math.floor(commentsB / COMMENTS_PER_VOTE);

  const ticks = [];
  for (let i = 0; i <= 20; i++) {
    const a = (180 + i * 9) * Math.PI / 180;
    const major = i % 5 === 0;
    ticks.push({
      x1: CX + (R - (major ? 15 : 10)) * Math.cos(a), y1: CY + (R - (major ? 15 : 10)) * Math.sin(a),
      x2: CX + (R - 4) * Math.cos(a), y2: CY + (R - 4) * Math.sin(a), major, isA: i < 10,
    });
  }

  const w = size === "large" ? "w-72 sm:w-80 md:w-[22rem]" : "w-44";

  return (
    <motion.div className="flex flex-col items-center select-none" data-testid="the-flip" whileHover={reduced ? undefined : { scale: 1.02 }} transition={{ type: "spring", stiffness: 300, damping: 20 }}>
      {!compact && (
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-[10px] font-bold tracking-[0.25em] uppercase text-neutral">THE FLIP</span>
          <span className="flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
            <span className="text-[9px] font-bold tracking-widest text-success uppercase">LIVE</span>
          </span>
        </div>
      )}

      <div className="relative">
        <svg viewBox="0 0 300 178" className={w}>
          <defs>
            <linearGradient id={`ga-${uid}`} x1="0" y1="1" x2="0" y2="0"><stop offset="0%" stopColor="#B07CE6" /><stop offset="100%" stopColor={BLUE} /></linearGradient>
            <linearGradient id={`gb-${uid}`} x1="0" y1="1" x2="0" y2="0"><stop offset="0%" stopColor="#FFE07A" /><stop offset="100%" stopColor={CORAL} /></linearGradient>
            <linearGradient id={`needle-${uid}`} x1="0" y1="1" x2="0" y2="0"><stop offset="0%" stopColor="#0D0F17" /><stop offset="65%" stopColor={lead} /><stop offset="100%" stopColor={lead} /></linearGradient>
            <filter id={`glow-${uid}`} x="-60%" y="-60%" width="220%" height="220%"><feGaussianBlur stdDeviation="3.2" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
            <filter id={`ds-${uid}`}><feDropShadow dx="0" dy="1.5" stdDeviation="2.5" floodOpacity="0.18" /></filter>
          </defs>

          <path d={`M ${trackL0[0]} ${trackL0[1]} A ${R} ${R} 0 0 1 ${trackL1[0]} ${trackL1[1]}`} fill="none" stroke="#E8EDF3" strokeWidth="17" strokeLinecap="round" />
          {ticks.map((t) => <line key={`tk-${t.x1.toFixed(1)}-${t.y1.toFixed(1)}`} x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2} stroke={t.isA ? "#C9A8ED" : "#FFE07A"} strokeWidth={t.major ? 1.6 : 0.8} opacity={t.major ? 0.65 : 0.3} />)}
          {fracA > 0.008 && <motion.path d={arcPath(0, fracA)} fill="none" stroke={`url(#ga-${uid})`} strokeWidth="17" strokeLinecap="round" style={{ filter: "drop-shadow(0 0 7px rgba(37,99,235,0.35))" }} initial={reduced ? false : { pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.9, ease: "easeInOut" }} />}
          {fracA < 0.992 && <motion.path d={arcPath(fracA, 1)} fill="none" stroke={`url(#gb-${uid})`} strokeWidth="17" strokeLinecap="round" style={{ filter: "drop-shadow(0 0 7px rgba(240,68,68,0.35))" }} initial={reduced ? false : { pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.9, ease: "easeInOut", delay: 0.12 }} />}

          {rippleKey > 0 && (
            <circle key={rippleKey} cx={CX} cy={CY} r="14" fill="none" stroke={lead} strokeWidth="2.5" opacity="0.5">
              <animate attributeName="r" from="14" to="96" dur="0.75s" fill="freeze" />
              <animate attributeName="opacity" from="0.5" to="0" dur="0.75s" fill="freeze" />
            </circle>
          )}

          {/* Ghost needle = votes-only position (visual cue for comment influence) */}
          {commentsCounted > 0 && Math.abs(commentDelta) >= 1 && (
            <g transform={`rotate(${ghostAngle} ${CX} ${CY})`} data-testid="votes-only-needle">
              <line x1={CX} y1={CY + 6} x2={CX} y2={CY - (NEEDLE - 6)} stroke="#94A3B8" strokeWidth="2.2" strokeDasharray="4 4" strokeLinecap="round" opacity="0.7" />
              <circle cx={CX} cy={CY - (NEEDLE - 6)} r="3" fill="#94A3B8" opacity="0.7" />
            </g>
          )}

          {/* Solid needle = votes + comments */}
          <g ref={needleRef} transform={`rotate(${targetAngle} ${CX} ${CY})`}>
            <polygon points={`${CX - 8},${CY + 10} ${CX},${CY - (NEEDLE + 4)} ${CX + 8},${CY + 10}`} fill={lead} opacity="0.22" filter={`url(#glow-${uid})`} />
            <line x1={CX} y1={CY + 6} x2={CX} y2={CY + 26} stroke="#172033" strokeWidth="5.5" strokeLinecap="round" opacity="0.85" />
            <circle cx={CX} cy={CY + 27} r="5" fill="#172033" opacity="0.85" />
            <polygon points={`${CX - 6.5},${CY + 8} ${CX},${CY - NEEDLE} ${CX + 6.5},${CY + 8}`} fill={`url(#needle-${uid})`} filter={`url(#ds-${uid})`} />
            <line x1={CX} y1={CY + 4} x2={CX} y2={CY - (NEEDLE - 4)} stroke="#fff" strokeWidth="1.1" opacity="0.55" strokeLinecap="round" />
            <motion.circle cx={CX} cy={CY - NEEDLE} r={6} fill={lead} initial={{ r: 6 }} animate={reduced ? { r: 6 } : { r: [5.5, 9, 5.5], opacity: [0.95, 0.45, 0.95] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }} />
            <circle cx={CX} cy={CY - NEEDLE} r="3" fill="#fff" />
          </g>

          <circle cx={CX} cy={CY} r="14" fill="white" stroke="#E2E8F0" strokeWidth="2" filter={`url(#ds-${uid})`} />
          <motion.circle cx={CX} cy={CY} r="6" fill={lead} animate={reduced ? { scale: 1 } : { scale: [1, 1.18, 1] }} transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }} style={{ transformOrigin: `${CX}px ${CY}px` }} />
        </svg>
      </div>

      <div className="flex items-end justify-between w-full max-w-[19rem] -mt-1">
        <div className="text-center flex-1 min-w-0 px-1">
          <div className="text-[9px] font-bold tracking-wider text-view-a uppercase leading-tight truncate" title={labelA}>{labelA}</div>
          <motion.span key={`a-${Math.round(pctA)}`} initial={{ scale: 1.3, opacity: 0.5 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 320, damping: 16 }} className="font-heading text-4xl sm:text-5xl text-view-a leading-none inline-block">{Math.round(pctA)}%</motion.span>
          {userSide === "a" && (
            <motion.div initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 400, damping: 15 }}
              className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold text-white bg-view-a px-2 py-0.5 rounded-full" data-testid="you-marker-a">You 👈</motion.div>
          )}
        </div>
        <span className="text-[11px] text-neutral font-medium pb-1.5 shrink-0"><CountUp value={voteTotal} /> votes</span>
        <div className="text-center flex-1 min-w-0 px-1">
          <div className="text-[9px] font-bold tracking-wider text-view-b uppercase leading-tight truncate" title={labelB}>{labelB}</div>
          <motion.span key={`b-${Math.round(pctB)}`} initial={{ scale: 1.3, opacity: 0.5 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 320, damping: 16 }} className="font-heading text-4xl sm:text-5xl text-view-b leading-none inline-block">{Math.round(pctB)}%</motion.span>
          {userSide === "b" && (
            <motion.div initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 400, damping: 15 }}
              className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold text-black bg-view-b px-2 py-0.5 rounded-full" data-testid="you-marker-b">👉 You</motion.div>
          )}
        </div>
      </div>

      {/* Influence legend / visual-cue explainer */}
      {!compact && (
      <div className="mt-2 flex flex-col items-center gap-1" data-testid="influence-legend">
        <div className="flex items-center gap-3 text-[10px] text-neutral">
          <span className="flex items-center gap-1"><svg width="18" height="6"><line x1="0" y1="3" x2="18" y2="3" stroke="#172033" strokeWidth="3" strokeLinecap="round" /></svg> votes + comments</span>
          <span className="flex items-center gap-1"><svg width="18" height="6"><line x1="0" y1="3" x2="18" y2="3" stroke="#94A3B8" strokeWidth="2" strokeDasharray="3 3" /></svg> votes only</span>
        </div>
        {commentsCounted > 0 && Math.abs(commentDelta) >= 1 ? (
          <p className="text-[11px] font-medium" style={{ color: commentDelta >= 0 ? BLUE : CORAL }}>
            Comments nudged the needle {commentDelta >= 0 ? "+" : ""}{commentDelta}% toward {commentDelta >= 0 ? labelA : labelB}
          </p>
        ) : (
          <p className="text-[10px] text-neutral/70">5 comments = 1 vote · comments sway the needle</p>
        )}
      </div>
      )}
    </motion.div>
  );
}
